import { Injector } from '@angular/core';
import { draggableNodeStopEvent } from '@examdojo/core/tiptap';
import { DOMNode, mergeAttributes, Node } from '@tiptap/core';
import { DOMParser, Fragment, Node as PmNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { AngularNodeViewRenderer } from 'ngx-tiptap';
import { DRAGGABLE_ITEM_NODE_NAME, DraggableItemExtension } from '../draggable-item';
import { KeyPointCalloutComponent } from './key-point-callout.component';
import { DEFAULT_KEY_POINT_CALLOUT_PROPS } from './key-point-callout.model';

export const KEY_POINT_CALLOUT_NODE_NAME = 'keyPointCallout';
const keyPointCalloutClassName = 'key-point-callout';

/**
 * Plugin that automatically wraps non-draggableItem content inside keyPointCallout nodes.
 * This ensures that when users press Enter or drag content into a callout, the content
 * gets properly wrapped in a draggableItem.
 */
const autoWrapDraggableItemPlugin = new Plugin({
  key: new PluginKey('autoWrapDraggableItem'),
  appendTransaction(transactions, oldState, newState) {
    const docChanged = transactions.some((tr) => tr.docChanged);
    if (!docChanged) {
      return null;
    }

    const { tr } = newState;
    let modified = false;

    newState.doc.descendants((node, pos) => {
      if (node.type.name !== KEY_POINT_CALLOUT_NODE_NAME) {
        return true;
      }

      const draggableItemType = newState.schema.nodes[DRAGGABLE_ITEM_NODE_NAME];

      node.forEach((child, offset) => {
        if (child.type.name !== DRAGGABLE_ITEM_NODE_NAME) {
          const start = pos + 1 + offset;
          const end = start + child.nodeSize;
          const mappedStart = tr.mapping.map(start);
          const mappedEnd = tr.mapping.map(end);

          const wrappedNode = draggableItemType.create(null, child);
          tr.replaceWith(mappedStart, mappedEnd, wrappedNode);
          modified = true;
        }
      });

      return false;
    });

    return modified ? tr : null;
  },
});

export const KeyPointCalloutExtension = (injector: Injector) => {
  return Node.create({
    name: KEY_POINT_CALLOUT_NODE_NAME,
    content: 'block+',
    group: 'block',
    defining: true,
    selectable: true,
    draggable: true,

    addExtensions() {
      return [DraggableItemExtension(injector)];
    },

    addProseMirrorPlugins() {
      return [autoWrapDraggableItemPlugin];
    },

    addAttributes() {
      return {
        title: {
          default: DEFAULT_KEY_POINT_CALLOUT_PROPS.title,
        },
        color: {
          default: DEFAULT_KEY_POINT_CALLOUT_PROPS.color,
        },
        icon: {
          default: DEFAULT_KEY_POINT_CALLOUT_PROPS.icon,
        },
        custom_css: {
          default: '',
        },
        custom_style: {
          default: null,
        },
        class: {
          renderHTML: (attributes) => {
            const otherClasses = ((attributes['class'] ?? '') as string)
              .split(' ')
              .filter((c) => c !== keyPointCalloutClassName);
            return { class: `${keyPointCalloutClassName} ${otherClasses.join(' ')}` };
          },
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: `div.${keyPointCalloutClassName}`,
          getContent(node: DOMNode, schema) {
            if (!(node instanceof Element)) {
              const draggableItemType = schema.nodes[DRAGGABLE_ITEM_NODE_NAME];
              const paragraphType = schema.nodes['paragraph'];
              const textContent = node.textContent || '';
              // Create empty paragraph if no text content (empty text nodes are not allowed in ProseMirror)
              const paragraph = textContent
                ? paragraphType.create(null, schema.text(textContent))
                : paragraphType.create(null);
              const draggableItem = draggableItemType.create(null, paragraph);
              return Fragment.from(draggableItem);
            }

            const contentElement = node.querySelector('.key-point-callout-content') ?? node;

            // Parse the content using DOMParser to get proper block nodes
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = contentElement.innerHTML;
            const parsedContent = DOMParser.fromSchema(schema).parse(tempDiv);

            // Wrap each block in a draggableItem
            const draggableItemType = schema.nodes[DRAGGABLE_ITEM_NODE_NAME];
            const wrappedNodes: PmNode[] = [];

            parsedContent.content.forEach((child) => {
              // If it's already a draggableItem, keep it as is
              if (child.type.name === DRAGGABLE_ITEM_NODE_NAME) {
                wrappedNodes.push(child);
              } else {
                // Wrap the block in a draggableItem
                const draggableItem = draggableItemType.create(null, child);
                wrappedNodes.push(draggableItem);
              }
            });

            return Fragment.from(wrappedNodes);
          },
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', mergeAttributes({ class: keyPointCalloutClassName }, HTMLAttributes), 0];
    },

    addNodeView() {
      return AngularNodeViewRenderer(KeyPointCalloutComponent, {
        injector,
        stopEvent: draggableNodeStopEvent,
      });
    },
  });
};
